/**
 * EVENTSFLO-CREW-PASSPORT-ID-DETAILS-OPTIONAL-TAB-AND-SCOPED-EXPORT-V1
 *
 * Optional Passport / ID tab for the admin Crew profile.
 *
 * - All fields are OPTIONAL.
 * - Missing data shows as "Not provided" badge — never an error.
 * - Saving is independent of the parent profile Save (dedicated PATCH).
 * - Not surfaced in the Client / Crew portals (V1).
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import { Loader2, Save, ShieldCheck, ShieldAlert, Info } from "lucide-react";
import {
  getStaffPassportDetails,
  patchStaffPassportDetails,
} from "../../lib/api";

const EMPTY = {
  identity_document_type: "",
  passport_last_name: "",
  passport_first_name: "",
  passport_date_of_birth: "",
  passport_nationality: "",
  passport_place_of_birth: "",
  passport_place_of_issue: "",
  passport_date_of_issue: "",
  passport_date_of_expiry: "",
  passport_number: "",
  passport_address: "",
};

const STATUS_META = {
  provided: {
    label: "Passport / ID provided",
    className:
      "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
    Icon: ShieldCheck,
  },
  partial: {
    label: "Passport / ID partial",
    className:
      "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30",
    Icon: ShieldAlert,
  },
  missing: {
    label: "Passport / ID missing",
    className:
      "bg-zinc-500/10 text-zinc-600 dark:text-zinc-300 border-zinc-500/30",
    Icon: Info,
  },
};

export function PassportIdTab({ staffId }) {
  const [form, setForm] = useState(EMPTY);
  const [completeness, setCompleteness] = useState("missing");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    if (!staffId) return;
    setLoading(true);
    try {
      const res = await getStaffPassportDetails(staffId);
      const pd = res.data?.passport_details || null;
      if (pd) {
        setForm({
          identity_document_type: pd.identity_document_type || "",
          passport_last_name: pd.passport_last_name || "",
          passport_first_name: pd.passport_first_name || "",
          passport_date_of_birth: pd.passport_date_of_birth || "",
          passport_nationality: pd.passport_nationality || "",
          passport_place_of_birth: pd.passport_place_of_birth || "",
          passport_place_of_issue: pd.passport_place_of_issue || "",
          passport_date_of_issue: pd.passport_date_of_issue || "",
          passport_date_of_expiry: pd.passport_date_of_expiry || "",
          passport_number: pd.passport_number || "",
          passport_address: pd.passport_address || "",
        });
        setUpdatedAt(pd.passport_details_updated_at || null);
      } else {
        setForm(EMPTY);
        setUpdatedAt(null);
      }
      setCompleteness(res.data?.completeness || "missing");
    } catch (err) {
      toast.error("Failed to load passport details");
    } finally {
      setLoading(false);
    }
  }, [staffId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Strip empty strings so backend stores nulls instead.
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === "" ? null : v]),
      );
      const res = await patchStaffPassportDetails(staffId, payload);
      setCompleteness(res.data?.completeness || "missing");
      setUpdatedAt(res.data?.passport_details?.passport_details_updated_at || null);
      toast.success("Passport / ID details saved");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
            : "Failed to save passport / ID details";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        className="flex items-center justify-center h-64 text-muted-foreground"
        data-testid="passport-tab-loading"
      >
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading…
      </div>
    );
  }

  const meta = STATUS_META[completeness] || STATUS_META.missing;
  const StatusIcon = meta.Icon;

  return (
    <div className="space-y-6" data-testid="passport-tab">
      {/* Header / completeness */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h3 className="text-base font-semibold text-foreground">
            Passport / ID Details
          </h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">
            Optional. Used only when clients, venues, yacht agents, or security
            teams request crew identity details before allowing crew on-site
            or on board.
          </p>
        </div>
        <Badge
          variant="outline"
          className={`gap-1.5 px-2.5 py-1 text-xs font-medium border ${meta.className}`}
          data-testid={`passport-completeness-${completeness}`}
        >
          <StatusIcon className="h-3.5 w-3.5" />
          {meta.label}
        </Badge>
      </div>

      {updatedAt && (
        <p
          className="text-xs text-muted-foreground"
          data-testid="passport-updated-at"
        >
          Last updated {new Date(updatedAt).toLocaleString()}
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Document type */}
        <div className="md:col-span-2">
          <Label className="text-secondary-foreground">Document Type</Label>
          <Select
            value={form.identity_document_type || "__none__"}
            onValueChange={(v) =>
              handleChange(
                "identity_document_type",
                v === "__none__" ? "" : v,
              )
            }
          >
            <SelectTrigger
              className="bg-input border-input text-foreground max-w-md"
              data-testid="passport-document-type-trigger"
            >
              <SelectValue placeholder="Not provided" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Not provided</SelectItem>
              <SelectItem value="passport">Passport</SelectItem>
              <SelectItem value="national_id">National ID</SelectItem>
              <SelectItem value="residence_permit">Residence Permit</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="pp-last-name">Surname / LAST NAME</Label>
          <Input
            id="pp-last-name"
            value={form.passport_last_name}
            onChange={(e) => handleChange("passport_last_name", e.target.value)}
            placeholder="As printed on the document"
            data-testid="passport-last-name-input"
            className="bg-input border-input"
          />
        </div>
        <div>
          <Label htmlFor="pp-first-name">First Name</Label>
          <Input
            id="pp-first-name"
            value={form.passport_first_name}
            onChange={(e) => handleChange("passport_first_name", e.target.value)}
            placeholder="As printed on the document"
            data-testid="passport-first-name-input"
            className="bg-input border-input"
          />
        </div>

        <div>
          <Label htmlFor="pp-dob">Date of Birth</Label>
          <Input
            id="pp-dob"
            type="date"
            value={form.passport_date_of_birth}
            onChange={(e) =>
              handleChange("passport_date_of_birth", e.target.value)
            }
            data-testid="passport-dob-input"
            className="bg-input border-input"
          />
        </div>
        <div>
          <Label htmlFor="pp-nationality">Nationality</Label>
          <Input
            id="pp-nationality"
            value={form.passport_nationality}
            onChange={(e) => handleChange("passport_nationality", e.target.value)}
            placeholder="e.g. French, British"
            data-testid="passport-nationality-input"
            className="bg-input border-input"
          />
        </div>

        <div>
          <Label htmlFor="pp-place-birth">Place of Birth</Label>
          <Input
            id="pp-place-birth"
            value={form.passport_place_of_birth}
            onChange={(e) =>
              handleChange("passport_place_of_birth", e.target.value)
            }
            placeholder="City / town as printed"
            data-testid="passport-place-of-birth-input"
            className="bg-input border-input"
          />
        </div>
        <div>
          <Label htmlFor="pp-place-issue">Place of Issue</Label>
          <Input
            id="pp-place-issue"
            value={form.passport_place_of_issue}
            onChange={(e) =>
              handleChange("passport_place_of_issue", e.target.value)
            }
            placeholder="Issuing authority / city"
            data-testid="passport-place-of-issue-input"
            className="bg-input border-input"
          />
        </div>

        <div>
          <Label htmlFor="pp-issue">Date of Issue</Label>
          <Input
            id="pp-issue"
            type="date"
            value={form.passport_date_of_issue}
            onChange={(e) =>
              handleChange("passport_date_of_issue", e.target.value)
            }
            data-testid="passport-date-of-issue-input"
            className="bg-input border-input"
          />
        </div>
        <div>
          <Label htmlFor="pp-expiry">Expiry Date</Label>
          <Input
            id="pp-expiry"
            type="date"
            value={form.passport_date_of_expiry}
            onChange={(e) =>
              handleChange("passport_date_of_expiry", e.target.value)
            }
            data-testid="passport-date-of-expiry-input"
            className="bg-input border-input"
          />
        </div>

        <div className="md:col-span-2">
          <Label htmlFor="pp-number">Passport / ID Number</Label>
          <Input
            id="pp-number"
            value={form.passport_number}
            onChange={(e) => handleChange("passport_number", e.target.value)}
            placeholder="Document number"
            autoComplete="off"
            data-testid="passport-number-input"
            className="bg-input border-input max-w-md"
          />
        </div>

        <div className="md:col-span-2">
          <Label htmlFor="pp-address">Address</Label>
          <Textarea
            id="pp-address"
            value={form.passport_address}
            onChange={(e) => handleChange("passport_address", e.target.value)}
            placeholder="Optional — address on document"
            rows={2}
            data-testid="passport-address-input"
            className="bg-input border-input"
          />
        </div>
      </div>

      <div className="flex justify-end pt-2 border-t border-border/40">
        <Button
          onClick={handleSave}
          disabled={saving}
          data-testid="passport-save-btn"
          className="gap-2"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          Save Passport / ID Details
        </Button>
      </div>
    </div>
  );
}

export default PassportIdTab;
